import { useCallback } from 'react';
import { useParams } from 'react-router-dom';

import GreenroomLogin from '@/connection/greenroom/greenroom-login';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { selectFeatureFlags } from '@/store/slices/feature-flags/selectors';
import { FeatureFlagsEnum } from '@/store/slices/feature-flags/types';
import { getToken } from '@/store/slices/user/selectors';
import toastTrigger from '@ui/toast/toast-trigger';
import { setLoadingGreenroom } from '@/store/slices/project';
import { getWorkingProjectSessions } from '@/store/slices/project/selectors';

export const useHandleGoToGreenroom = () => {
  const dispatch = useAppDispatch();
  const params = useParams();

  const sessions = useAppSelector(getWorkingProjectSessions);
  const token = useAppSelector(getToken);
  const flags = useAppSelector(selectFeatureFlags);

  const unifiedAuth = flags?.[FeatureFlagsEnum.unified_auth];

  const isSingleSession = sessions && sessions.length === 1;
  const session = isSingleSession ? sessions?.[0] : sessions?.find((s) => s.uuid === params.sessionUuid);

  return useCallback(() => {
    (async () => {
      if (!token || !session?.uuid) return;
      try {
        if (unifiedAuth) {
          window.open(`${import.meta.env.VITE_GREENROOM_REDIRECT}/create/${session.uuid}`, '_blank');
          return;
        }

        dispatch(setLoadingGreenroom(true));
        const result = await GreenroomLogin();
        if (!result.token) {
          throw new Error('No token returned from greenroom login');
        }

        const greenroomUrl = `${import.meta.env.VITE_GREENROOM_REDIRECT}/login-with-streams-token/${result.token}?page=/create/${session.uuid}`;
        window.open(greenroomUrl, '_blank');
      } catch (e) {
        console.error(e);
        toastTrigger(
          <div className="S1">
            <div>There was an error logging into Greenroom.</div>
            <div>Please try again.</div>
          </div>,
          {
            type: 'error',
            autoClose: 10000,
          }
        );
      }

      dispatch(setLoadingGreenroom(false));
    })();
  }, [dispatch, session?.uuid, token, unifiedAuth]);
};
